// Calendar tab of the Assessment page (src/pages/Assessment.js).
// Pure code-motion (Tech Debt 2026-09 "Assessment and Backtest are god
// files" ticket) -- self-contained: its own fetch (/api/backtest/temporal)
// and loading/error state, matching the ticket's target shape. No behaviour
// change from the version formerly inline in Assessment.js.
import React, { useState, useEffect } from 'react';
import { Activity } from 'lucide-react';
import { winRateColor } from '../../utils/colors';
import { ddBasisLabel } from '../../utils/format';
import {
  cacheBustBucket, CALL_BE, PUT_BE, blendedBE, MONTH_ABBR, heatColor,
  fmtReturn, fmtPpDelta, deltaColor, Delta, returnColor, SectionHeader, ScrollTable,
} from './shared';

export default function CalendarTab({ apiBase, versionId, activeVersionId, dteStrategy = '30', portfolioProfile = 'core', refreshNonce = 0, onProfiles }) {
  const [tData, setTData]   = useState(null);
  const [tLoad, setTLoad]   = useState(true);
  const [tErr,  setTErr]    = useState(null);
  const [view,  setView]    = useState('combined'); // 'combined' | 'calls' | 'puts'
  const [activeData, setActiveData] = useState(null);

  useEffect(() => {
    setTLoad(true);
    setTErr(null);
    setTData(null);
    const params = new URLSearchParams();
    if (versionId) params.set('version', versionId);
    if (dteStrategy === '15') params.set('dte', '15');
    params.set('profile', portfolioProfile);
    params.set('_t', cacheBustBucket());
    const url = `${apiBase}/api/backtest/temporal${params.toString() ? '?' + params.toString() : ''}`;
    fetch(url)
      .then(r => r.json().then(d => ({ ok: r.ok, d })))
      .then(({ ok, d }) => {
        if (!ok) throw new Error(d.error || `HTTP error`);
        if (Array.isArray(d.portfolio_profiles?.profiles)) onProfiles?.(d.portfolio_profiles.profiles);
        setTData(d);
        setTLoad(false);
      })
      .catch(e => { setTErr(e.message); setTLoad(false); });
  }, [apiBase, versionId, dteStrategy, portfolioProfile, refreshNonce, onProfiles]);

  // When a historical version is selected, fetch the active version's temporal
  // stats so each value can show a delta vs active.
  useEffect(() => {
    if (!activeVersionId || !versionId || activeVersionId === versionId) {
      setActiveData(null);
      return;
    }
    let cancelled = false;
    const params = new URLSearchParams();
    if (dteStrategy === '15') params.set('dte', '15');
    params.set('profile', portfolioProfile);
    params.set('_t', cacheBustBucket());
    fetch(`${apiBase}/api/backtest/temporal?${params.toString()}`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (!cancelled && d && !d.error) setActiveData(d); })
      .catch(() => { if (!cancelled) setActiveData(null); });
    return () => { cancelled = true; };
  }, [apiBase, versionId, activeVersionId, dteStrategy, portfolioProfile, refreshNonce]);

  if (tLoad) return (
    <div className="flex items-center justify-center py-20 text-gray-500 text-sm gap-2">
      <Activity className="w-4 h-4 animate-spin" />
      Loading…
    </div>
  );
  if (tErr) return (
    <div className="py-10 text-center">
      <p className="text-trading-red-400 font-medium mb-1">No backtest data available</p>
      <p className="text-gray-500 text-sm">{tErr}</p>
      <p className="text-gray-600 text-xs mt-2">Run <code className="bg-white/[0.06] px-1 py-0.5 rounded font-mono">trader temporal-refresh --profile {portfolioProfile}</code> to generate.</p>
    </div>
  );

  if (!tData) return null;
  const { yearly = [], monthly = [], monthly_avg = [], summary = {} } = tData;
  const portfolioWindows = tData.portfolio_windows?.windows || [];
  const profileLabel = tData.portfolio_profile?.name || portfolioProfile;

  // Average return per year across the yearly rows (ignoring nulls)
  const yearlyReturns = yearly.map(y => y.return_pct).filter(v => v != null);
  const avgReturnPerYear = yearlyReturns.length
    ? yearlyReturns.reduce((s, v) => s + v, 0) / yearlyReturns.length
    : null;

  // Build month grid: {year -> {month -> row}}
  const monthByYearMo = {};
  monthly.forEach(m => {
    if (!monthByYearMo[m.year]) monthByYearMo[m.year] = {};
    monthByYearMo[m.year][m.month] = m;
  });
  const gridYears = [...new Set(monthly.map(m => m.year))].sort((a, b) => b - a);
  // monthly_avg keyed by calendar month (1-12)
  const avgByMonth = {};
  monthly_avg.forEach(a => { avgByMonth[a.month] = a; });

  // Active-version lookup maps for delta comparison (only set when viewing
  // a non-active version and the active fetch completed).
  const showDelta = !!activeData;
  const activeYearByYear = {};
  const activeMonthByYM  = {};
  const activeAvgByMonth = {};
  let activeAvgReturnPerYear = null;
  let activeSummary = {};
  const activeWindowByName = {};
  if (showDelta) {
    activeSummary = activeData.summary || {};
    (activeData.yearly || []).forEach(y => { activeYearByYear[y.year] = y; });
    (activeData.monthly || []).forEach(m => {
      activeMonthByYM[`${m.year}-${m.month}`] = m;
    });
    (activeData.monthly_avg || []).forEach(a => { activeAvgByMonth[a.month] = a; });
    (activeData.portfolio_windows?.windows || []).forEach(w => { activeWindowByName[w.name] = w; });
    const activeReturns = (activeData.yearly || [])
      .map(y => y.return_pct).filter(v => v != null);
    activeAvgReturnPerYear = activeReturns.length
      ? activeReturns.reduce((s, v) => s + v, 0) / activeReturns.length
      : null;
  }
  // Pick the active equivalent of `tpForView` for arbitrary rows
  function activeTpForView(row) {
    if (!row) return null;
    if (view === 'calls') return row.call_tp_rate;
    if (view === 'puts')  return row.put_tp_rate;
    return row.tp_rate;
  }
  const diff = (a, b) => (a == null || b == null ? null : a - b);
  // Drawdown basis (2026-09-27): rows refreshed since then are mark-to-market, older
  // rows cost basis. A DD delta across the two bases is meaningless -- suppress it.
  const ddBasis = tData.dd_basis || summary.dd_basis || 'cost';
  const activeDdBasis = activeData?.dd_basis || activeSummary.dd_basis || 'cost';
  const ddDiff = (a, b, basisA, basisB) => ((basisA || 'cost') === (basisB || 'cost') ? diff(a, b) : null);

  // Determine bar scale for yearly chart
  const allRates = yearly.flatMap(y => [y.tp_rate, y.call_tp_rate, y.put_tp_rate].filter(Boolean));
  const maxRate  = allRates.length ? Math.max(...allRates) : 100;

  function barPct(val) {
    return val != null ? Math.min(100, (val / Math.max(maxRate, 80)) * 100) : 0;
  }

  function tpForView(row) {
    if (view === 'calls') return row.call_tp_rate;
    if (view === 'puts')  return row.put_tp_rate;
    return row.tp_rate;
  }
  function beForRow(row) {
    if (view === 'puts')  return PUT_BE;
    if (view === 'calls') return CALL_BE;
    return blendedBE(row);
  }
  function windowRangeLabel(row) {
    const start = row.start || row.window?.start;
    const end = row.end || row.window?.end;
    return `${start || '—'} → ${end || 'now'}`;
  }
  function sourceLabel(source) {
    if (source === 'research_pack') return 'pack';
    if (source === 'temporal_monthly') return 'monthly';
    return 'pending';
  }

  return (
    <div className="space-y-8">
      {/* ── Methodology banner — single-path replay disclaimer ─────────────── */}
      <div className="bg-amber-500/[0.06] border border-amber-500/30 rounded px-4 py-3 space-y-1.5">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-medium uppercase tracking-[0.18em] text-amber-300">
            Deterministic single-path replay
          </span>
          <span className="text-[10px] text-gray-600 font-mono">backtest_cascade.py</span>
        </div>
        <p className="text-[12px] text-gray-400 leading-relaxed">
          These numbers come from <span className="text-gray-200 font-medium">one chronological replay</span> through real OHLCV
          with score-descending tiebreaks — a single realization, not an MC distribution. The "avg return/yr" and "DD" figures
          below are useful as a "the math compounds" sanity check, but they
          {' '}<span className="text-gray-200 font-medium">do not match the ship-gate metrics</span> in version-history.md
          (which use N=500 × 8-window MC). At the compound scale this strategy produces, single-path return numbers can vary
          by orders of magnitude across paths that have similar MC distributions.
        </p>
        <p className="text-[11px] text-gray-500 leading-relaxed">
          For decision-making, prefer <span className="text-gray-300">5y MC WorstDD</span> (Stage 3 T4 ship gate) and
          {' '}<span className="text-gray-300">per-trade TP% / WR15 by tier</span> (Stage 1 W1 ship gate) over compound returns at this scale.
        </p>
      </div>
      {showDelta && (
        <p className="text-[11px] text-gray-500 -mt-2">
          Δ values shown next to each metric are <span className="text-gray-300">this version − active version</span>;
          green = better, red = worse (drawdown inverted).
        </p>
      )}
      {/* Controls row */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Side filter */}
        <div className="flex items-center border border-white/[0.06] rounded-md overflow-hidden">
          {[['combined','All'],['calls','Calls'],['puts','Puts']].map(([v, lbl], i) => (
            <button
              key={v}
              onClick={() => setView(v)}
              className={`px-2.5 py-1 text-[11px] font-medium transition-colors whitespace-nowrap ${
                i > 0 ? 'border-l border-white/[0.06]' : ''
              } ${v === view ? 'bg-white/[0.06] text-white' : 'text-gray-500 hover:text-gray-300'}`}
            >
              {lbl}
            </button>
          ))}
        </div>
        {/* Summary pills */}
        {summary.n_trades != null && (
          <div className="flex items-center gap-3 ml-auto text-[11px] text-gray-500">
            <span>{summary.n_trades?.toLocaleString()} trades</span>
            <span className="text-gray-700">·</span>
            {avgReturnPerYear != null && (
              <>
                <span className="text-gray-400" title="Single-path replay — one realization of MC noise. Not the ship-gate metric. See banner above.">
                  avg return/yr (1-path)
                </span>
                <span className={avgReturnPerYear >= 0 ? 'text-trading-green-400' : 'text-trading-red-400'}>
                  {fmtReturn(avgReturnPerYear)}
                </span>
                {showDelta && (
                  <Delta
                    value={diff(avgReturnPerYear, activeAvgReturnPerYear)}
                    kind="return"
                  />
                )}
                <span className="text-gray-700">·</span>
              </>
            )}
            <span title={`Max drawdown of the single replay path (${ddBasisLabel(ddBasis)}). DD survives translation to real execution; compound does not. Use 5y MC WorstDD as the ship-gate metric.`}>
              DD (1-path) {summary.max_dd?.toFixed(1)}%
            </span>
            <span className="text-gray-600">{ddBasisLabel(ddBasis)}</span>
            {showDelta && (
              <Delta
                value={ddDiff(summary.max_dd, activeSummary.max_dd, ddBasis, activeDdBasis)}
                invert
              />
            )}
            <span className="text-gray-700">·</span>
            <span className="text-gray-400">
              profile {profileLabel}
            </span>
            {tData.computed_at && (
              <>
                <span className="text-gray-700">·</span>
                <span title={`Backtest temporal stats last refreshed ${new Date(tData.computed_at).toLocaleString()}`}>
                  refreshed {new Date(tData.computed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  {' '}{new Date(tData.computed_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                </span>
              </>
            )}
          </div>
        )}
      </div>

      {/* ── Named portfolio windows ──────────────────────────────────────── */}
      <div>
        <SectionHeader>Portfolio stress windows</SectionHeader>
        <ScrollTable minWidth={760}>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/[0.08]">
                <th className="text-left py-2 px-3 text-[10px] font-medium uppercase tracking-[0.14em] text-gray-600">Window</th>
                <th className="text-right py-2 px-2 text-[10px] font-medium uppercase tracking-[0.14em] text-gray-600">Return</th>
                <th className="text-right py-2 px-2 text-[10px] font-medium uppercase tracking-[0.14em] text-gray-600">Max DD</th>
                <th className="text-right py-2 px-2 text-[10px] font-medium uppercase tracking-[0.14em] text-gray-600">Trades</th>
                <th className="text-right py-2 px-2 text-[10px] font-medium uppercase tracking-[0.14em] text-gray-600">TP%</th>
                <th className="text-right py-2 px-2 text-[10px] font-medium uppercase tracking-[0.14em] text-gray-600">Calls</th>
                <th className="text-right py-2 px-2 text-[10px] font-medium uppercase tracking-[0.14em] text-gray-600">Puts</th>
                <th className="text-right py-2 px-3 text-[10px] font-medium uppercase tracking-[0.14em] text-gray-600">Source</th>
              </tr>
            </thead>
            <tbody>
              {portfolioWindows.map(w => {
                const m = w.metrics || {};
                const activeW = showDelta ? activeWindowByName[w.name] : null;
                const activeM = activeW?.metrics || {};
                const dRet = showDelta ? diff(m.total_return_pct, activeM.total_return_pct) : null;
                const dDd = showDelta ? ddDiff(m.max_dd_pct, activeM.max_dd_pct, m.dd_basis, activeM.dd_basis) : null;
                const dTp = showDelta ? diff(m.tp_rate, activeM.tp_rate) : null;
                const status = w.ready ? 'ready' : 'waiting';
                const statusClass = w.ready
                  ? 'bg-trading-green-400/10 text-trading-green-300 border-trading-green-400/20'
                  : 'bg-amber-400/8 text-amber-300 border-amber-400/20';
                const title = (w.missing || []).join('; ') || w.description || '';
                return (
                  <tr key={w.name} className="border-b border-white/[0.04] hover:bg-white/[0.02]">
                    <td className="py-2 px-3">
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-[12px] font-medium text-gray-200 truncate">{w.label || w.name}</span>
                          <span
                            title={title}
                            className={`inline-flex items-center border rounded px-1.5 py-0.5 text-[9px] uppercase tracking-[0.12em] ${statusClass}`}
                          >
                            {status}
                          </span>
                        </div>
                        <span className="text-[10px] text-gray-600 font-mono tabular-nums">{windowRangeLabel(w)}</span>
                      </div>
                    </td>
                    <td className={`py-2 px-2 text-right text-[12px] font-mono ${m.total_return_pct != null ? returnColor(m.total_return_pct) : 'text-gray-600'}`}>
                      <span className="inline-flex items-center gap-1 justify-end">
                        {m.total_return_pct != null ? fmtReturn(m.total_return_pct) : '—'}
                        {showDelta && <Delta value={dRet} kind="return" />}
                      </span>
                    </td>
                    <td className={`py-2 px-2 text-right text-[12px] font-mono ${m.max_dd_pct != null ? (m.max_dd_pct <= 70 ? 'text-trading-green-400' : m.max_dd_pct <= 80 ? 'text-amber-300' : 'text-trading-red-400') : 'text-gray-600'}`}>
                      <span className="inline-flex items-center gap-1 justify-end" title={m.max_dd_peak_date && m.max_dd_trough_date ? `${m.max_dd_peak_date} → ${m.max_dd_trough_date} · ${ddBasisLabel(m.dd_basis)}` : m.max_dd_source || ''}>
                        {m.max_dd_pct != null ? `${m.max_dd_pct.toFixed(1)}%` : '—'}
                        {showDelta && <Delta value={dDd} invert />}
                      </span>
                    </td>
                    <td className="py-2 px-2 text-right text-[12px] text-gray-500 tabular-nums">{m.n_trades?.toLocaleString?.() || '—'}</td>
                    <td className={`py-2 px-2 text-right text-[12px] font-mono ${winRateColor(m.tp_rate)}`}>
                      <span className="inline-flex items-center gap-1 justify-end">
                        {m.tp_rate != null ? `${m.tp_rate.toFixed(1)}%` : '—'}
                        {showDelta && <Delta value={dTp} />}
                      </span>
                    </td>
                    <td className="py-2 px-2 text-right text-[12px] text-gray-500 tabular-nums">{m.call_trades?.toLocaleString?.() || '—'}</td>
                    <td className="py-2 px-2 text-right text-[12px] text-gray-500 tabular-nums">{m.put_trades?.toLocaleString?.() || '—'}</td>
                    <td className="py-2 px-3 text-right text-[10px] uppercase tracking-[0.12em] text-gray-600">{sourceLabel(w.metric_source)}</td>
                  </tr>
                );
              })}
              {portfolioWindows.length === 0 && (
                <tr>
                  <td colSpan={8} className="py-6 text-center text-sm text-gray-600">No portfolio windows available</td>
                </tr>
              )}
            </tbody>
          </table>
        </ScrollTable>
      </div>

      {/* ── Year-by-year chart ────────────────────────────────────────────── */}
      <div>
        <SectionHeader>Year-by-year TP rate</SectionHeader>
        <div className="space-y-1.5">
          {yearly.map(yr => {
            const tp = tpForView(yr);
            const be = beForRow(yr);
            const bePct = barPct(be);
            const activeYr = showDelta ? activeYearByYear[yr.year] : null;
            const tpDelta  = showDelta ? diff(tp, activeTpForView(activeYr)) : null;
            const retDelta = showDelta ? diff(yr.return_pct, activeYr?.return_pct) : null;
            return (
              <div key={yr.year} className="flex items-center gap-3">
                <span className="text-[12px] font-mono tabular-nums text-gray-400 w-12 text-right flex-shrink-0">
                  {yr.year}
                </span>
                {/* Bar track */}
                <div className="relative flex-1 h-6 bg-white/[0.03] rounded overflow-hidden">
                  {/* Break-even marker */}
                  <div
                    className="absolute top-0 bottom-0 w-px bg-white/20"
                    style={{ left: `${bePct}%` }}
                    title={view === 'combined'
                      ? `BE ${be.toFixed(1)}% (blended: ${yr.call_n || 0} calls @ ${CALL_BE}% + ${yr.put_n || 0} puts @ ${PUT_BE}%)`
                      : `BE ${be}%`}
                  />
                  {/* TP bar */}
                  <div
                    className={`absolute left-0 top-0 bottom-0 rounded transition-all ${
                      tp != null && tp >= be ? 'bg-trading-green-400/40' : 'bg-trading-red-400/40'
                    }`}
                    style={{ width: `${barPct(tp)}%` }}
                  />
                  {/* TP label */}
                  {tp != null && (
                    <span className={`absolute left-2 top-1/2 -translate-y-1/2 text-[11px] font-mono tabular-nums font-medium flex items-center gap-1.5 ${
                      tp >= be ? 'text-trading-green-300' : 'text-trading-red-400'
                    }`}>
                      {tp.toFixed(1)}%
                      {showDelta && <Delta value={tpDelta} />}
                    </span>
                  )}
                </div>
                {/* Meta */}
                <div className="flex items-center gap-2 text-[11px] tabular-nums w-36 flex-shrink-0 justify-end">
                  <span className="text-gray-600">{yr.n_trades.toLocaleString()}t</span>
                  <span className={`${yr.return_pct >= 0 ? 'text-trading-green-400' : 'text-trading-red-400'} font-mono`}>
                    {fmtReturn(yr.return_pct)}
                  </span>
                  {showDelta && <Delta value={retDelta} kind="return" />}
                </div>
              </div>
            );
          })}
          {yearly.length === 0 && (
            <p className="text-gray-600 text-sm text-center py-6">No data</p>
          )}
        </div>
        <p className="mt-2 text-[11px] text-gray-600">
          White line = break-even TP rate ({view === 'puts' ? `${PUT_BE}%` : view === 'calls' ? `${CALL_BE}%` : `${PUT_BE}%/${CALL_BE}% blended per row by call/put mix`}).
          Bars left of the line are below break-even.
        </p>
      </div>

      {/* ── Monthly heatmap ───────────────────────────────────────────────── */}
      <div>
        <SectionHeader>Monthly TP rate heatmap</SectionHeader>
        <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
          <table className="text-[11px] tabular-nums border-separate border-spacing-[2px]" style={{ minWidth: 560 }}>
            <thead>
              <tr>
                <th className="text-right pr-2 pb-1 font-normal text-gray-600 w-12">Year</th>
                {MONTH_ABBR.map(m => (
                  <th key={m} className="text-center pb-1 font-normal text-gray-600 w-10">{m}</th>
                ))}
                <th className="text-right pl-2 pb-1 font-normal text-gray-600 w-14">Return</th>
              </tr>
            </thead>
            <tbody>
              {gridYears.map(yr => {
                const yrRow = yearly.find(y => y.year === yr);
                const activeYr = showDelta ? activeYearByYear[yr] : null;
                const retDelta = showDelta ? diff(yrRow?.return_pct, activeYr?.return_pct) : null;
                return (
                  <tr key={yr}>
                    <td className="text-right pr-2 font-mono text-gray-500">{yr}</td>
                    {Array.from({ length: 12 }, (_, idx) => {
                      const mo  = idx + 1;
                      const row = monthByYearMo[yr]?.[mo];
                      const tp  = row ? tpForView(row) : null;
                      const n   = row?.n_trades ?? 0;
                      const { bg, text } = heatColor(tp, view === 'puts' ? 'put' : view === 'calls' ? 'call' : 'combined', row);
                      const activeRow = showDelta ? activeMonthByYM[`${yr}-${mo}`] : null;
                      const tpDelta = showDelta ? diff(tp, activeTpForView(activeRow)) : null;
                      const titleBase = row
                        ? `${MONTH_ABBR[idx]} ${yr}: ${tp != null ? tp.toFixed(1) + '% TP' : 'n/a'} · ${n} trades`
                        : `${MONTH_ABBR[idx]} ${yr}: no data`;
                      const titleText = showDelta && tpDelta != null
                        ? `${titleBase} · vs active ${fmtPpDelta(tpDelta)}`
                        : titleBase;
                      return (
                        <td key={mo} className="p-0">
                          <div
                            className={`flex flex-col items-center justify-center rounded w-10 h-9 ${bg} cursor-default`}
                            title={titleText}
                          >
                            {tp != null ? (
                              <>
                                <span className={`${text} font-medium leading-none`}>{tp.toFixed(0)}%</span>
                                {showDelta && tpDelta != null ? (
                                  <span className={`${deltaColor(tpDelta)} text-[8px] leading-none mt-0.5 font-mono tabular-nums`}>
                                    {fmtPpDelta(tpDelta)}
                                  </span>
                                ) : (
                                  <span className="text-gray-600 text-[9px] leading-none mt-0.5">{n}</span>
                                )}
                              </>
                            ) : (
                              <span className="text-gray-800 text-[9px]">—</span>
                            )}
                          </div>
                        </td>
                      );
                    })}
                    <td className="text-right pl-2 font-mono">
                      {yrRow ? (
                        <span className="inline-flex items-center gap-1">
                          <span className={yrRow.return_pct >= 0 ? 'text-trading-green-400' : 'text-trading-red-400'}>
                            {fmtReturn(yrRow.return_pct)}
                          </span>
                          {showDelta && <Delta value={retDelta} kind="return" />}
                        </span>
                      ) : '—'}
                    </td>
                  </tr>
                );
              })}
              {gridYears.length === 0 && (
                <tr>
                  <td colSpan={14} className="text-center text-gray-600 py-8">No monthly data</td>
                </tr>
              )}
              {/* ── Cross-year average row ── */}
              {monthly_avg.length > 0 && (
                <tr className="border-t border-white/[0.08]">
                  <td className="text-right pr-2 font-medium text-[11px] text-gray-500 pt-1">Avg</td>
                  {Array.from({ length: 12 }, (_, idx) => {
                    const mo  = idx + 1;
                    const row = avgByMonth[mo];
                    const tp  = row ? tpForView(row) : null;
                    const n   = row?.n_trades ?? 0;
                    const { bg, text } = heatColor(tp, view === 'puts' ? 'put' : view === 'calls' ? 'call' : 'combined');
                    const activeAvg = showDelta ? activeAvgByMonth[mo] : null;
                    const tpDelta = showDelta ? diff(tp, activeTpForView(activeAvg)) : null;
                    const titleBase = row
                      ? `${MONTH_ABBR[idx]} avg (${row.years_sampled}y): ${tp != null ? tp.toFixed(1) + '% TP' : 'n/a'} · ${n} trades`
                      : `${MONTH_ABBR[idx]}: no data`;
                    const titleText = showDelta && tpDelta != null
                      ? `${titleBase} · vs active ${fmtPpDelta(tpDelta)}`
                      : titleBase;
                    return (
                      <td key={mo} className="p-0 pt-1">
                        <div
                          className={`flex flex-col items-center justify-center rounded w-10 h-9 ${bg} cursor-default ring-1 ring-white/[0.06]`}
                          title={titleText}
                        >
                          {tp != null ? (
                            <>
                              <span className={`${text} font-semibold leading-none`}>{tp.toFixed(0)}%</span>
                              {showDelta && tpDelta != null ? (
                                <span className={`${deltaColor(tpDelta)} text-[8px] leading-none mt-0.5 font-mono tabular-nums`}>
                                  {fmtPpDelta(tpDelta)}
                                </span>
                              ) : (
                                <span className="text-gray-500 text-[9px] leading-none mt-0.5">{row.years_sampled}y</span>
                              )}
                            </>
                          ) : (
                            <span className="text-gray-800 text-[9px]">—</span>
                          )}
                        </div>
                      </td>
                    );
                  })}
                  <td className="text-right pl-2 font-mono text-gray-600 text-[11px] pt-1">—</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="flex items-center gap-4 mt-3 flex-wrap">
          <span className="text-[10px] uppercase tracking-[0.14em] text-gray-600">Legend</span>
          {[
            { bg: 'bg-trading-green-400/25', label: '≥75%' },
            { bg: 'bg-trading-green-400/15', label: '65–75%' },
            { bg: 'bg-trading-green-400/10', label: `BE+5 – 65%` },
            { bg: 'bg-white/[0.04]',         label: `~BE` },
            { bg: 'bg-amber-400/8',          label: 'Below BE' },
            { bg: 'bg-trading-red-400/12',   label: 'Well below BE' },
          ].map(({ bg, label }) => (
            <div key={label} className="flex items-center gap-1.5">
              <div className={`w-3 h-3 rounded-sm ${bg} border border-white/[0.06]`} />
              <span className="text-[11px] text-gray-500">{label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Monthly detail table ─────────────────────────────────────────── */}
      <div>
        <SectionHeader>Month-by-month detail</SectionHeader>
        <ScrollTable minWidth={520}>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/[0.08]">
                <th className="text-left py-2 px-3 text-[10px] font-medium uppercase tracking-[0.14em] text-gray-600">Month</th>
                <th className="text-right py-2 px-2 text-[10px] font-medium uppercase tracking-[0.14em] text-gray-600">Trades</th>
                <th className="text-right py-2 px-2 text-[10px] font-medium uppercase tracking-[0.14em] text-gray-600">TP%</th>
                <th className="text-right py-2 px-2 text-[10px] font-medium uppercase tracking-[0.14em] text-gray-600">Call TP%</th>
                <th className="text-right py-2 px-2 text-[10px] font-medium uppercase tracking-[0.14em] text-gray-600">Put TP%</th>
                <th className="text-right py-2 px-2 text-[10px] font-medium uppercase tracking-[0.14em] text-gray-600">Return</th>
              </tr>
            </thead>
            <tbody>
              {[...monthly].reverse().map(m => {
                const label = `${MONTH_ABBR[m.month - 1]} ${m.year}`;
                const a = showDelta ? activeMonthByYM[`${m.year}-${m.month}`] : null;
                const dTp     = showDelta ? diff(m.tp_rate,      a?.tp_rate)      : null;
                const dCallTp = showDelta ? diff(m.call_tp_rate, a?.call_tp_rate) : null;
                const dPutTp  = showDelta ? diff(m.put_tp_rate,  a?.put_tp_rate)  : null;
                const dRet    = showDelta ? diff(m.return_pct,   a?.return_pct)   : null;
                return (
                  <tr key={label} className="border-b border-white/[0.04] hover:bg-white/[0.02]">
                    <td className="py-1.5 px-3 font-mono tabular-nums text-gray-400 text-[12px]">{label}</td>
                    <td className="py-1.5 px-2 text-right text-gray-500 text-[12px]">{m.n_trades}</td>
                    <td className={`py-1.5 px-2 text-right text-[12px] font-mono ${winRateColor(m.tp_rate)}`}>
                      <span className="inline-flex items-center gap-1 justify-end">
                        {m.tp_rate != null ? `${m.tp_rate.toFixed(1)}%` : '—'}
                        {showDelta && <Delta value={dTp} />}
                      </span>
                    </td>
                    <td className={`py-1.5 px-2 text-right text-[12px] font-mono ${winRateColor(m.call_tp_rate)}`}>
                      <span className="inline-flex items-center gap-1 justify-end">
                        {m.call_tp_rate != null ? `${m.call_tp_rate.toFixed(1)}%` : '—'}
                        {showDelta && <Delta value={dCallTp} />}
                      </span>
                    </td>
                    <td className={`py-1.5 px-2 text-right text-[12px] font-mono ${winRateColor(m.put_tp_rate)}`}>
                      <span className="inline-flex items-center gap-1 justify-end">
                        {m.put_tp_rate != null ? `${m.put_tp_rate.toFixed(1)}%` : '—'}
                        {showDelta && <Delta value={dPutTp} />}
                      </span>
                    </td>
                    <td className={`py-1.5 px-2 text-right text-[12px] font-mono ${m.return_pct != null ? returnColor(m.return_pct) : 'text-gray-600'}`}>
                      <span className="inline-flex items-center gap-1 justify-end">
                        {m.return_pct != null ? fmtReturn(m.return_pct) : '—'}
                        {showDelta && <Delta value={dRet} kind="return" />}
                      </span>
                    </td>
                  </tr>
                );
              })}
              {/* ── Per-calendar-month averages ── */}
              {monthly_avg.length > 0 && (
                <>
                  <tr className="border-t border-white/[0.10]">
                    <td colSpan={6} className="py-1.5 px-3 text-[10px] uppercase tracking-[0.14em] text-gray-600 font-medium">
                      Average by calendar month
                    </td>
                  </tr>
                  {monthly_avg.map(a => {
                    const av = showDelta ? activeAvgByMonth[a.month] : null;
                    const dTp     = showDelta ? diff(a.tp_rate,      av?.tp_rate)      : null;
                    const dCallTp = showDelta ? diff(a.call_tp_rate, av?.call_tp_rate) : null;
                    const dPutTp  = showDelta ? diff(a.put_tp_rate,  av?.put_tp_rate)  : null;
                    return (
                      <tr key={`avg-${a.month}`} className="border-b border-white/[0.03] hover:bg-white/[0.02]">
                        <td className="py-1.5 px-3 font-mono tabular-nums text-gray-400 text-[12px]">
                          {MONTH_ABBR[a.month - 1]}
                          <span className="text-gray-700 ml-1 text-[10px]">({a.years_sampled}y)</span>
                        </td>
                        <td className="py-1.5 px-2 text-right text-gray-500 text-[12px]">{a.n_trades ?? '—'}</td>
                        <td className={`py-1.5 px-2 text-right text-[12px] font-mono ${winRateColor(a.tp_rate)}`}>
                          <span className="inline-flex items-center gap-1 justify-end">
                            {a.tp_rate != null ? `${a.tp_rate.toFixed(1)}%` : '—'}
                            {showDelta && <Delta value={dTp} />}
                          </span>
                        </td>
                        <td className={`py-1.5 px-2 text-right text-[12px] font-mono ${winRateColor(a.call_tp_rate)}`}>
                          <span className="inline-flex items-center gap-1 justify-end">
                            {a.call_tp_rate != null ? `${a.call_tp_rate.toFixed(1)}%` : '—'}
                            {showDelta && <Delta value={dCallTp} />}
                          </span>
                        </td>
                        <td className={`py-1.5 px-2 text-right text-[12px] font-mono ${winRateColor(a.put_tp_rate)}`}>
                          <span className="inline-flex items-center gap-1 justify-end">
                            {a.put_tp_rate != null ? `${a.put_tp_rate.toFixed(1)}%` : '—'}
                            {showDelta && <Delta value={dPutTp} />}
                          </span>
                        </td>
                        <td className="py-1.5 px-2 text-right text-gray-600 text-[12px]">—</td>
                      </tr>
                    );
                  })}
                </>
              )}
            </tbody>
          </table>
        </ScrollTable>
      </div>
    </div>
  );
}
